/**
 * PATH: packages/email-hub/src/feature/reader-header/triage/TriageSummary.tsx
 * FILE_ID: EMAILHUB-READERHEADER-TRIAGE-SUMMARY-001
 * PURPOSE: Render the resolved 32px summary line: hue dots + Company ·
 *   Category · Signals · N placements, the placements control, and a chevron.
 * ROLE IN ARCHITECTURE: Presentational leaf of the triage band (plan D269-13).
 * MAIN EXPORTS: ReaderTriageSummary, TriageChevron.
 * NON-RESPONSIBILITIES: Expansion state and mutation controls live in callers.
 * NOTES FOR FUTURE AI: The line is a HARD 32px (SUMMARY_H) — segments truncate,
 *   never wrap; adding a segment requires a matching hue dot entry.
 */
import type { ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '../../../_internal/lib/utils'
import type { ReaderContextSummary } from '../model/types'
import { SUMMARY_H } from '../tokens'
import { SUMMARY_SEGMENT_DOTS } from './hues'

export interface TriageChevronProps {
  expanded: boolean
  onToggle: () => void
}

/** Shared expand/collapse chevron; aria points at the expanded context region. */
export function TriageChevron({ expanded, onToggle }: TriageChevronProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={cn(
        'relative inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-1.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-1',
        // 28px control in a 32px band — cannot grow without overflowing the
        // summary line, so the coarse-pointer target is a transparent
        // pseudo-element rather than real box growth (WORK ITEM 7).
        "before:absolute before:inset-0 before:content-[''] pointer-coarse:before:inset-[-8px]",
      )}
      aria-expanded={expanded}
      aria-controls="reader-expanded-context"
      title={expanded ? 'Collapse context' : 'Show full context'}
    >
      <span>{expanded ? 'Less' : 'Context'}</span>
      <ChevronDown
        className={cn(
          'h-3 w-3 transition-transform duration-150 motion-reduce:transition-none',
          expanded && 'rotate-180',
        )}
      />
    </button>
  )
}

interface SummarySegment {
  key: keyof typeof SUMMARY_SEGMENT_DOTS
  text: string
}

/** Compose the bounded segment list from the context summary. Pure I → O. */
function summarySegments(
  context: ReaderContextSummary,
  includePlacements: boolean,
): SummarySegment[] {
  const segments: SummarySegment[] = []
  if (context.company) segments.push({ key: 'company', text: context.company })
  if (context.classification) {
    segments.push({ key: 'classification', text: context.classification })
  }
  if (context.signalCount > 0) {
    segments.push({
      key: 'signals',
      text: `${context.signalCount} signal${context.signalCount === 1 ? '' : 's'}`,
    })
  }
  if (includePlacements && context.placementCount > 0) {
    segments.push({
      key: 'placements',
      text: `${context.placementCount} placement${context.placementCount === 1 ? '' : 's'}`,
    })
  }
  return segments
}

export interface ReaderTriageSummaryProps {
  context: ReaderContextSummary
  expanded: boolean
  onToggle: () => void
  /** Interactive placements chip (AllocationChip-based); it IS the Placement segment. */
  placementsControl?: ReactNode
}

export function ReaderTriageSummary({
  context,
  expanded,
  onToggle,
  placementsControl,
}: ReaderTriageSummaryProps) {
  // With an interactive chip present, the "N placements" text would duplicate it.
  const segments = summarySegments(context, !placementsControl)

  return (
    <div
      className="group flex items-center gap-1.5 overflow-hidden px-3"
      style={{ height: SUMMARY_H }}
      data-testid="reader-context-summary"
    >
      {/* The hue dots and the segment run ARE the second way into the lanes.
          They used to be inert text, so on a phone — where the row is too tight
          to also show the chevron, and the lanes are only reachable through it —
          the operator was left pressing dots that do nothing and reported the
          Company/Class/Intel groups as missing (2026-08-14). Whatever states
          the context also opens it. */}
      <button
        type="button"
        onClick={onToggle}
        data-testid="reader-context-summary-open"
        aria-expanded={expanded}
        aria-controls="reader-expanded-context"
        title={expanded ? 'Collapse context' : 'Show full context'}
        className={cn(
          'flex h-full min-w-0 shrink items-center gap-1.5 overflow-hidden rounded-md px-1 text-left',
          'hover:bg-accent focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-primary/50 focus-visible:ring-offset-1',
        )}
      >
        {segments.length > 0 && (
          <span className="flex shrink-0 items-center gap-0.5" aria-hidden>
            {segments.map((segment) => (
              <span
                key={segment.key}
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  SUMMARY_SEGMENT_DOTS[segment.key],
                )}
              />
            ))}
          </span>
        )}
        {segments.length > 0 ? (
          <span className="flex min-w-0 shrink items-center gap-1 overflow-hidden text-[11px] text-muted-foreground">
            {segments.map((segment, index) => (
              <span
                key={segment.key}
                className="flex min-w-0 items-center gap-1"
                data-reader-summary-segment={segment.key}
              >
                {index > 0 && <span aria-hidden>·</span>}
                <span className="min-w-0 truncate" title={segment.text}>
                  {segment.text}
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className="whitespace-nowrap text-[10px] text-muted-foreground">
            No context assigned
          </span>
        )}
      </button>
      {placementsControl && (
        // SHRINKABLE, not shrink-0. The placement chip carries a full
        // breadcrumb ("Job #2094 · Service Today Pty Ltd · Site"); at shrink-0
        // it kept that width at a phone reader and pushed the chevron beside it
        // out through the row's overflow-hidden edge, which removed the only
        // route to the lanes. It has its own min-w-0 + overflow-hidden inside,
        // so yielding here truncates the breadcrumb instead of hiding a control.
        <span className="flex min-w-0 shrink items-center overflow-hidden">
          {placementsControl}
        </span>
      )}
      <span className="ml-auto flex shrink-0 items-center">
        <TriageChevron expanded={expanded} onToggle={onToggle} />
      </span>
    </div>
  )
}
